import { Box, Card, CardContent, CardMedia, Typography, Button, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getKdramaCreditAggregates } from './API';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { useTheme } from '@mui/system';

function CreditList() {
    const { id } = useParams();
    const [credits, setCredits] = useState({ cast: [], crew: [] });
    const [visibleCount, setVisibleCount] = useState(8);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Vérification pour mobile

    const navToActorDetails = (actorId) => {
        navigate(`/actor/${actorId}/${credits.cast.find(actor => actor.id === actorId).name}`);
    };

    useEffect(() => {
        const fetchCreditsList = async () => {
            try {
                const data = await getKdramaCreditAggregates(id);
                setCredits(data);
            } catch (error) {
                console.error('Erreur lors de la recherche des crédits:', error);
            }
        };

        fetchCreditsList();
    }, [id]);

    if (!credits) {
        return <LoadingSpinner size={60} color="white" text="Chargement des données..." />;
    }

    const showMore = () => {
        setVisibleCount(prevCount => prevCount + 8);
    };

    return (
        <Box sx={{ marginTop: '2rem', padding: isMobile ? '0 1rem' : '0 2rem', mb: '2rem' }}>
            {/* Cast Section */}
            <Typography variant="h5" sx={{ fontFamily: "SUSE, sans-serif", color: 'white', marginBottom: '1.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                Distribution des rôles
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: isMobile ? 2 : 3 }}>
                {credits.cast.slice(0, visibleCount).map((actor) => (
                    <Card
                        key={actor.id}
                        onClick={() => navToActorDetails(actor.id)}
                        sx={{
                            maxWidth: isMobile ? 120 : 150, // Ajuste la largeur pour mobile
                            cursor: 'pointer',
                            boxShadow: 3,
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: isMobile ? 'none' : 'scale(1.05)', // Pas de hover sur mobile
                                boxShadow: isMobile ? 3 : 6
                            },
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                        {actor.profile_path ? (
                            <CardMedia
                                component="img"
                                image={`https://image.tmdb.org/t/p/w200${actor.profile_path}`}
                                alt={actor.name}
                                sx={{ height: 225 }}
                            />
                        ) : (
                            <Box sx={{
                                height: 225,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#ddd',
                                color: '#555',
                                fontSize: '2rem',
                                fontWeight: 'bold'
                            }}>
                                {actor.name.charAt(0)}
                            </Box>
                        )}
                        <CardContent sx={{ padding: '8px', textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontFamily: "SUSE, sans-serif", fontSize: '1rem', fontWeight: 'bold' }}>
                                {actor.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "SUSE, sans-serif", fontSize: '0.85rem' }}>
                                {actor.roles[0]?.character || 'Rôle inconnu'}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>
            {visibleCount < credits.cast.length && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                    <Button variant="contained" color="primary" sx={{ fontFamily: "SUSE, sans-serif" }} onClick={showMore}>
                        Voir Plus
                    </Button>
                </Box>
            )}

            {/* Crew Section */}
            <Typography variant="h5" sx={{ fontFamily: "SUSE, sans-serif", color: 'white', marginTop: '3rem', marginBottom: '1.5rem', textAlign: 'center', fontWeight: 'bold' }}>
                Équipe de production
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: isMobile ? 2 : 3 }}>
                {credits.crew.slice(0, visibleCount).map((crew) => (
                    <Card
                        key={crew.id}
                        sx={{
                            maxWidth: isMobile ? 120 : 150, // Ajuste la largeur pour mobile
                            boxShadow: 3,
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: isMobile ? 'none' : 'scale(1.05)', // Pas de hover sur mobile
                                boxShadow: isMobile ? 3 : 6
                            },
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                        {crew.profile_path ? (
                            <CardMedia
                                component="img"
                                image={`https://image.tmdb.org/t/p/w200${crew.profile_path}`}
                                alt={crew.name}
                                sx={{ height: 225 }}
                            />
                        ) : (
                            <Box sx={{
                                height: 225,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#ddd',
                                color: '#555',
                                fontSize: '2rem',
                                fontWeight: 'bold'
                            }}>
                                {crew.name.charAt(0)}
                            </Box>
                        )}
                        <CardContent sx={{ padding: '8px', textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontFamily: "SUSE, sans-serif", fontSize: '1rem', fontWeight: 'bold' }}>
                                {crew.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontFamily: "SUSE, sans-serif", fontSize: '0.85rem' }}>
                                {crew.jobs[0]?.job || 'Rôle inconnu'}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>
            {visibleCount < credits.crew.length && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                    <Button variant="contained" sx={{ fontFamily: "SUSE, sans-serif", backgroundColor: '#2596BE' }} onClick={showMore}>
                        Voir Plus
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default CreditList;
